import React from "react";
import { Avatar, Flex, Text } from "@fluentui/react-northstar";
import styled from "styled-components/macro";
import { CalendarChat20Regular } from "@fluentui/react-icons";
import { I18N_NS_EXPENSE } from "../../constants";
import { useTranslation } from "react-i18next";
import moment from "moment";
import OneComment from "../../components/Cards/OneComment";

/**
 * @typedef {Object} Comment
 * @property {string} user_fullname - The full name of the user who made the comment.
 * @property {string} user_avatar - The URL of the user's avatar image.
 * @property {string} date - The date of the comment.
 * @property {"expense_edited" | "expense_added"} type - The type of the comment (e.g., "expense_edited").
 * @property {string} description - The description or content of the comment.
 */

/**
 * EvolutionComment Component
 *
 * This component renders a list of comments with user details and timestamps.
 * Each comment includes the user's avatar, full name, date, and a description.
 * The component is styled using styled-components and Fluent UI components.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Comment[]} props.comments - An array of comment objects to display.
 *
 * @returns {React.Element} The rendered EvolutionComment component.
 *
 * @example
 * const comments = [
 *   {
 *     user_fullname: "John Doe",
 *     user_avatar: "https://example.com/avatar.jpg",
 *     date: "2023-03-15",
 *     type: "expense_edited",
 *     description: "Updated the expense details."
 *   },
 *   {
 *     user_fullname: "Jane Smith",
 *     user_avatar: "https://example.com/avatar2.jpg",
 *     date: "2023-03-16",
 *     type: "expense_added",
 *     description: "Added a new expense."
 *   }
 * ];
 *
 * <EvolutionComment comments={comments} />
 */
const EvolutionComment = ({ comments = [] }) => {
    const { t } = useTranslation(I18N_NS_EXPENSE);
    return (
        <Container>
            <Flex column hAlign="center" gap="gap.medium" fill>
                <Flex column gap="gap.small" fill>
                    {comments.map((comment, index) => {
                        return (
                            <OneComment
                                key={index}
                                comment={{
                                    ...comment,
                                }}
                                isDescriptionComponent={true}
                            />
                        );
                    })}
                </Flex>
            </Flex>
        </Container>
    );
};

export default EvolutionComment;

const Container = styled(Flex)`
    width: clamp(100%, 2vw, 479px);
    padding: 16px 8px;
`;
